import { useEffect, useState } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLIDE_LABELS = {
    top: "Cover",
    problem: "Problem",
    solution: "Solution",
    product: "8 Dimensions",
    metrics: "Proprietary Metrics",
    dashboard: "Match Dashboard",
    pricing: "Pricing",
    competitive: "Competitive Edge",
    traction: "Traction",
    team: "Team",
    cta: "Close",
};

function fmtMs(ms) {
    if (!ms) return "0s";
    const s = Math.round(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rs = s % 60;
    return `${m}m ${rs}s`;
}

export default function AnalyticsPage() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const key = params.get("key");
        if (!key) {
            setErr("Missing ?key= parameter");
            setLoading(false);
            return;
        }
        fetch(`${API}/analytics/summary?key=${encodeURIComponent(key)}`)
            .then((r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.json();
            })
            .then((d) => {
                setData(d);
                setLoading(false);
            })
            .catch((e) => {
                setErr(e.message || "Failed");
                setLoading(false);
            });
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#04140D] grid place-items-center text-white">
                <div className="text-white/55">Loading analytics…</div>
            </div>
        );
    }

    if (err) {
        return (
            <div className="min-h-screen bg-[#04140D] grid place-items-center text-white px-6">
                <div className="max-w-md text-center">
                    <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#FF3B30] mb-3">
                        Access denied
                    </div>
                    <div className="font-display font-bold text-3xl mb-3">
                        Analytics dashboard
                    </div>
                    <p className="text-white/55 text-sm mb-6">
                        This dashboard is protected. Append{" "}
                        <span className="font-mono text-[#D4FF00]">?key=YOUR_KEY</span> to
                        the URL.
                    </p>
                    <p className="text-white/35 text-xs">Error: {err}</p>
                </div>
            </div>
        );
    }

    const totals = data?.totals || {};
    const bySlide = (data?.by_slide || []).map((r) => ({
        ...r,
        label: SLIDE_LABELS[r.slide_id] || r.slide_id,
    }));
    const byLang = data?.by_lang || [];

    const maxDwell = Math.max(...bySlide.map((r) => r.total_dwell_ms), 1);

    return (
        <div className="min-h-screen bg-[#04140D] text-white px-6 md:px-12 py-12 md:py-16">
            <div className="max-w-[1200px] mx-auto">
                <div className="flex items-center justify-between mb-12">
                    <div>
                        <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#D4FF00] mb-2">
                            Davini.ai · Pitch deck analytics
                        </div>
                        <h1 className="font-display font-bold uppercase text-4xl md:text-5xl tracking-tighter">
                            Audience engagement
                        </h1>
                    </div>
                    <a
                        href="/"
                        className="text-xs uppercase tracking-widest font-bold text-white/55 hover:text-[#D4FF00]"
                    >
                        ← Back to deck
                    </a>
                </div>

                {/* KPI row */}
                <div className="grid sm:grid-cols-3 gap-4 mb-10">
                    <Kpi label="Unique sessions" value={totals.total_sessions ?? 0} />
                    <Kpi label="Slide views" value={totals.total_events ?? 0} />
                    <Kpi
                        label="Total time on deck"
                        value={fmtMs(totals.total_dwell_ms)}
                    />
                </div>

                {/* Dwell by slide */}
                <div className="rounded-2xl border border-white/10 bg-[#071B12] p-6 md:p-8 mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#D4FF00] mb-1.5">
                                Dwell time per slide
                            </div>
                            <div className="font-display font-bold text-xl tracking-tight">
                                Where the audience lingers
                            </div>
                        </div>
                    </div>
                    {bySlide.length === 0 ? (
                        <div className="py-12 text-center text-white/40 text-sm">
                            No data yet — once recruiters/investors view the deck, their
                            dwell time will appear here.
                        </div>
                    ) : (
                        <>
                            <div className="h-[320px] mb-4">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={bySlide}
                                        layout="vertical"
                                        margin={{ left: 20 }}
                                    >
                                        <CartesianGrid
                                            stroke="rgba(255,255,255,0.06)"
                                            horizontal={false}
                                        />
                                        <XAxis
                                            type="number"
                                            tick={{
                                                fill: "rgba(255,255,255,0.45)",
                                                fontSize: 10,
                                            }}
                                            axisLine={false}
                                            tickLine={false}
                                            tickFormatter={(v) => fmtMs(v)}
                                        />
                                        <YAxis
                                            type="category"
                                            dataKey="label"
                                            width={140}
                                            tick={{
                                                fill: "rgba(255,255,255,0.75)",
                                                fontSize: 11,
                                                fontWeight: 600,
                                            }}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                background: "#04140D",
                                                border: "1px solid rgba(255,255,255,0.1)",
                                                borderRadius: 8,
                                                fontSize: 12,
                                            }}
                                            formatter={(v, name) => {
                                                if (name === "total_dwell_ms")
                                                    return [fmtMs(v), "Total dwell"];
                                                return [v, name];
                                            }}
                                            labelStyle={{ color: "#D4FF00" }}
                                        />
                                        <Bar
                                            dataKey="total_dwell_ms"
                                            fill="#D4FF00"
                                            radius={[0, 6, 6, 0]}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Detail table */}
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-sm">
                                    <thead>
                                        <tr className="text-[10px] uppercase tracking-widest text-white/45 font-bold border-b border-white/5">
                                            <th className="py-3">Slide</th>
                                            <th className="py-3 text-right">Sessions</th>
                                            <th className="py-3 text-right">Views</th>
                                            <th className="py-3 text-right">Avg dwell</th>
                                            <th className="py-3 text-right">Total dwell</th>
                                            <th className="py-3 w-[140px]"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bySlide.map((r) => (
                                            <tr
                                                key={r.slide_id}
                                                className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]"
                                            >
                                                <td className="py-3 font-semibold text-white">
                                                    {r.label}
                                                </td>
                                                <td className="py-3 text-right tabular text-white/85">
                                                    {r.sessions}
                                                </td>
                                                <td className="py-3 text-right tabular text-white/85">
                                                    {r.views}
                                                </td>
                                                <td className="py-3 text-right tabular text-white/85">
                                                    {fmtMs(r.avg_dwell_ms)}
                                                </td>
                                                <td className="py-3 text-right tabular text-[#D4FF00] font-bold">
                                                    {fmtMs(r.total_dwell_ms)}
                                                </td>
                                                <td className="py-3 pl-3">
                                                    <div className="h-1.5 rounded-full bg-[#0E2B1E] overflow-hidden">
                                                        <div
                                                            className="h-full rounded-full bg-[#D4FF00]"
                                                            style={{
                                                                width: `${Math.max(4, Math.round((r.total_dwell_ms / maxDwell) * 100))}%`,
                                                            }}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>

                {/* Language breakdown */}
                {byLang.length > 0 && (
                    <div className="rounded-2xl border border-white/10 bg-[#071B12] p-6 md:p-8">
                        <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#D4FF00] mb-1.5">
                            Language breakdown
                        </div>
                        <div className="font-display font-bold text-xl tracking-tight mb-5">
                            Who's reading in which language
                        </div>
                        <div className="flex flex-wrap gap-3">
                            {byLang.map((r) => (
                                <div
                                    key={r.lang}
                                    className="rounded-lg border border-white/10 bg-[#04140D] px-4 py-3 min-w-[120px]"
                                >
                                    <div className="text-[10px] uppercase tracking-widest text-white/45">
                                        {r.lang}
                                    </div>
                                    <div className="font-display font-bold text-2xl tabular text-[#D4FF00]">
                                        {r.views}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-widest text-white/35">
                                        views
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function Kpi({ label, value }) {
    return (
        <div className="rounded-2xl border border-white/10 bg-[#071B12] p-6">
            <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#D4FF00] mb-2">
                {label}
            </div>
            <div className="font-display font-bold tabular text-4xl text-white">
                {value}
            </div>
        </div>
    );
}
